import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid
} from 'recharts';
import {
  X, Building2, AlertTriangle, AlertCircle, CheckCircle2,
  Wrench, Zap, Droplets, Sofa, ThumbsUp, TrendingUp, TrendingDown,
  ChevronRight, Lightbulb, Star, BarChart3
} from 'lucide-react';

const SCORE_LABELS = {
  fluidite: { label: 'Fluidité', icon: Zap, color: '#8b5cf6' },
  equipement: { label: 'Équipement', icon: Wrench, color: '#f59e0b' },
  proprete: { label: 'Propreté', icon: Droplets, color: '#06b6d4' },
  confort: { label: 'Confort', icon: Sofa, color: '#10b981' },
  satisfaction: { label: 'Satisfaction', icon: ThumbsUp, color: '#ec4899' },
};

const getScoreColor = (score) => {
  if (score >= 4) return { bg: 'bg-emerald-50', text: 'text-emerald-600', bar: '#10b981' };
  if (score >= 3) return { bg: 'bg-amber-50', text: 'text-amber-600', bar: '#f59e0b' };
  return { bg: 'bg-red-50', text: 'text-red-600', bar: '#ef4444' };
};

const severityConfig = {
  probleme_majeur: { label: 'Majeur', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  probleme_mineur: { label: 'Mineur', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
};
const statusConfig = {
  nouveau: { label: 'Nouveau', color: 'text-red-600 bg-red-50' },
  en_cours: { label: 'En cours', color: 'text-amber-600 bg-amber-50' },
  resolu: { label: 'Résolu', color: 'text-emerald-600 bg-emerald-50' },
};

const MONTH_LABELS = {
  '01': 'Jan', '02': 'Fév', '03': 'Mar', '04': 'Avr', '05': 'Mai', '06': 'Jun',
  '07': 'Jul', '08': 'Aoû', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Déc'
};

function formatPeriod(period) {
  if (!period) return '';
  const [year, month] = period.split('-');
  return `${MONTH_LABELS[month] || month} ${year}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{formatPeriod(label)}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="font-bold" style={{ color: p.color }}>{p.value}/5</span>
        </div>
      ))}
    </div>
  );
};

export const PropertyDetailModal = ({ property, onClose }) => {
  const api = useApi();
  const [detail, setDetail] = useState(null);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [recurrences, setRecurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [detailRes, historyRes, recurrenceRes] = await Promise.all([
          api.get(`/quality/property/${property.property_id}`),
          api.get(`/quality/property/${property.property_id}/monthly-history`),
          api.get(`/quality/recurrence/${property.property_id}`)
        ]);
        setDetail(detailRes.data);
        setMonthlyHistory(historyRes.data || []);
        setRecurrences(recurrenceRes.data?.recurrences || []);
      } catch {
        toast.error('Erreur lors du chargement des détails');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [api, property.property_id]);

  const radarData = Object.entries(SCORE_LABELS).map(([key, { label }]) => ({
    dimension: label,
    score: property.scores[key],
    fullMark: 5
  }));

  // Compute trend between last two periods
  const trendData = monthlyHistory.length >= 2
    ? {
        current: monthlyHistory[monthlyHistory.length - 1],
        previous: monthlyHistory[monthlyHistory.length - 2],
      }
    : null;

  const chartData = monthlyHistory.map(m => ({
    period: m.period,
    label: formatPeriod(m.period),
    Global: m.scores.global,
    Fluidité: m.scores.fluidite,
    Équipement: m.scores.equipement,
    Propreté: m.scores.proprete,
    Confort: m.scores.confort,
    Satisfaction: m.scores.satisfaction,
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" data-testid="property-detail-modal">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto animate-slideInRight">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FFF0F1] flex items-center justify-center">
              <Building2 className="h-5 w-5 text-[#FF5A6E]" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900 text-sm truncate max-w-[280px]">{property.property_name}</h2>
              <p className="text-xs text-slate-400">{property.bookings_analyzed} séjours analysés</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="close-modal-btn">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Global score + Radar */}
          <div className="flex items-start gap-6">
            <div className="flex-1">
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 8, fill: '#94a3b8' }} />
                  <Radar dataKey="score" stroke="#FF5A6E" fill="#FF5A6E" fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col items-center gap-1">
              {(() => {
                const c = getScoreColor(property.scores.global);
                return (
                  <div className={`w-20 h-20 rounded-2xl ${c.bg} flex flex-col items-center justify-center`}>
                    <span className={`text-3xl font-bold ${c.text}`}>{property.scores.global}</span>
                    <span className="text-[10px] text-slate-400">/5</span>
                  </div>
                );
              })()}
              <span className="text-xs text-slate-500 font-medium mt-1">Score global</span>
              {/* Trend badge */}
              {trendData && (() => {
                const diff = +(trendData.current.scores.global - trendData.previous.scores.global).toFixed(1);
                if (Math.abs(diff) < 0.1) return null;
                const isUp = diff > 0;
                return (
                  <div className={`flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    isUp ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
                  }`}>
                    {isUp ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {isUp ? '+' : ''}{diff}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Monthly Evolution Chart */}
          {monthlyHistory.length > 0 && (
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="w-full flex items-center justify-between"
                  data-testid="toggle-history-btn"
                >
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <BarChart3 size={14} className="text-[#FF5A6E]" />
                    Évolution mensuelle
                    <span className="text-[10px] font-normal text-slate-400 ml-1">({monthlyHistory.length} mois)</span>
                  </CardTitle>
                  <ChevronRight size={14} className={`text-slate-400 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
                </button>
              </CardHeader>
              {showHistory && (
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="period"
                        tickFormatter={formatPeriod}
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                      />
                      <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        iconType="circle"
                        iconSize={6}
                        wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
                      />
                      <Line type="monotone" dataKey="Global" stroke="#FF5A6E" strokeWidth={2.5} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Fluidité" stroke="#8b5cf6" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                      <Line type="monotone" dataKey="Équipement" stroke="#f59e0b" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                      <Line type="monotone" dataKey="Propreté" stroke="#06b6d4" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                      <Line type="monotone" dataKey="Confort" stroke="#10b981" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                      <Line type="monotone" dataKey="Satisfaction" stroke="#ec4899" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                    </LineChart>
                  </ResponsiveContainer>

                  {/* Period comparison table */}
                  {trendData && (
                    <div className="mt-4 border border-slate-100 rounded-lg overflow-hidden">
                      <div className="grid grid-cols-3 text-[10px] font-semibold text-slate-500 bg-slate-50 px-3 py-2">
                        <span>Catégorie</span>
                        <span className="text-center">{formatPeriod(trendData.previous.period)}</span>
                        <span className="text-center">{formatPeriod(trendData.current.period)}</span>
                      </div>
                      {['global', ...Object.keys(SCORE_LABELS)].map(key => {
                        const label = key === 'global' ? 'Score global' : SCORE_LABELS[key]?.label;
                        const prev = trendData.previous.scores[key];
                        const curr = trendData.current.scores[key];
                        const diff = +(curr - prev).toFixed(1);
                        return (
                          <div key={key} className={`grid grid-cols-3 text-xs px-3 py-1.5 border-t border-slate-50 ${key === 'global' ? 'bg-slate-50 font-semibold' : ''}`}>
                            <span className="text-slate-600">{label}</span>
                            <span className="text-center text-slate-500">{prev}</span>
                            <span className={`text-center font-medium flex items-center justify-center gap-1 ${
                              diff > 0.1 ? 'text-emerald-600' : diff < -0.1 ? 'text-red-600' : 'text-slate-500'
                            }`}>
                              {curr}
                              {diff > 0.1 && <TrendingUp size={10} />}
                              {diff < -0.1 && <TrendingDown size={10} />}
                              {Math.abs(diff) > 0.1 && <span className="text-[9px]">({diff > 0 ? '+' : ''}{diff})</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Score breakdown */}
          <Card className="bg-slate-50 border-slate-100">
            <CardContent className="p-4 space-y-3">
              {Object.entries(SCORE_LABELS).map(([key, { label, icon: Icon }]) => {
                const score = property.scores[key];
                const c = getScoreColor(score);
                return (
                  <div key={key} className="flex items-center gap-3">
                    <Icon size={14} className={c.text} />
                    <span className="text-xs text-slate-600 w-24">{label}</span>
                    <div className="flex-1 h-2 bg-white rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${(score / 5) * 100}%`, backgroundColor: c.bar }} />
                    </div>
                    <span className={`text-xs font-bold w-8 text-right ${c.text}`}>{score}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Improvement actions */}
          {property.top_improvements?.length > 0 && (
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Lightbulb size={14} className="text-amber-500" />
                  Actions d'amélioration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {property.top_improvements.map((action, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="w-5 h-5 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      {action}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Positive points */}
          {property.top_positives?.length > 0 && (
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Star size={14} className="text-emerald-500" />
                  Points forts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {property.top_positives.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      {point}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Recurring issues */}
          {!loading && recurrences.filter(r => r.is_recurring).length > 0 && (
            <Card className="bg-white border-red-100 border-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle size={14} className="text-red-500" />
                  Problèmes récurrents
                  <span className="ml-auto text-[10px] font-normal text-red-400">Signalés par plusieurs voyageurs</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-red-50">
                  {recurrences.filter(r => r.is_recurring).map((rec, i) => (
                    <div key={i} className="p-3" data-testid={`recurring-issue-${i}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          rec.severity === 'probleme_majeur' ? 'bg-red-100' : 'bg-amber-100'
                        }`}>
                          <span className="text-xs font-bold text-red-600">{rec.guest_count}x</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800">{rec.summary}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-red-50 text-red-600">
                              {rec.guest_count} voyageur{rec.guest_count > 1 ? 's' : ''}
                            </span>
                            {(rec.guests || []).slice(0, 3).map((g, j) => (
                              <span key={j} className="text-[10px] text-slate-400">{g}</span>
                            ))}
                            {(rec.guests || []).length > 3 && (
                              <span className="text-[10px] text-slate-400">+{(rec.guests || []).length - 3}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Open issues */}
          {loading ? (
            <div className="text-center py-4 text-sm text-slate-400">Chargement des problèmes...</div>
          ) : detail?.issues?.length > 0 ? (
            <Card className="bg-white border-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle size={14} className="text-red-500" />
                  Problèmes signalés ({detail.issues.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {(detail?.issues || []).slice(0, 10).map(issue => {
                    const rec = recurrences.find(r => r.issue_ids?.includes(issue.id));
                    return (
                    <Link
                      key={issue.id}
                      to={`/issues/${issue.id}`}
                      onClick={onClose}
                      className="flex items-start gap-3 p-3 hover:bg-slate-50 transition-colors"
                      data-testid={`modal-issue-${issue.id}`}
                    >
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                        issue.issue_severity === 'probleme_majeur' ? 'bg-red-100' : 'bg-amber-100'
                      }`}>
                        {issue.issue_severity === 'probleme_majeur'
                          ? <AlertTriangle size={12} className="text-red-500" />
                          : <AlertCircle size={12} className="text-amber-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-800">{issue.issue_summary || 'Problème détecté'}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${statusConfig[issue.tracking_status]?.color || ''}`}>
                            {statusConfig[issue.tracking_status]?.label}
                          </span>
                          <span className="text-[10px] text-slate-400">{issue.guest_name}</span>
                          {rec && rec.is_recurring && (
                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-50 text-red-600">
                              {rec.guest_count}x
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-slate-300 mt-1" />
                    </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-4 text-sm text-slate-400 flex items-center justify-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-400" />
              Aucun problème signalé pour cette propriété
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
